import {
  type AreaData,
  ColorType,
  type IChartApi,
  type ISeriesApi,
  type UTCTimestamp,
  createChart,
} from 'lightweight-charts'
import { useEffect, useMemo, useRef, useState } from 'react'

import ArcsTokenIcon from './ArcsTokenIcon'
import { DataStatistic } from './DataStatistic'

const TAB_ORDER = ['1H', '1D', '1W', '1M', '1Y', 'All'] as const
export type MarketRange = (typeof TAB_ORDER)[number]

type MarketSeries = AreaData<UTCTimestamp>
export type MarketSeriesMap = Record<MarketRange, MarketSeries[]>

const seconds = {
  minute: 60,
  hour: 60 * 60,
  day: 24 * 60 * 60,
  week: 7 * 24 * 60 * 60,
}

const BASE_EPOCH = Math.floor(new Date('2024-01-01T00:00:00Z').getTime() / 1000)

const createSeries = (
  length: number,
  stepSeconds: number,
  amplitude: number,
  trend: number
): MarketSeries[] => {
  return Array.from({ length }, (_, index) => {
    const time = (BASE_EPOCH + index * stepSeconds) as UTCTimestamp
    const value = Number((1.2 + Math.sin(index / 8) * amplitude + index * trend).toFixed(3))
    return { time, value }
  })
}

const DEFAULT_DATA: MarketSeriesMap = {
  '1H': createSeries(60, seconds.minute, 0.012, 0.00005),
  '1D': createSeries(96, seconds.hour / 4, 0.035, 0.0004),
  '1W': createSeries(168, seconds.hour, 0.05, 0.001),
  '1M': createSeries(180, seconds.hour * 4, 0.08, 0.0012),
  '1Y': createSeries(104, seconds.week, 0.12, 0.002),
  All: createSeries(156, seconds.week, 0.15, 0.0025),
}

export interface MarketCardProps {
  tokenLabel?: string
  currentPriceLabel?: string
  priceChangeLabel?: string
  isPriceChangePositive?: boolean
  data?: Partial<MarketSeriesMap>
  initialRange?: MarketRange
  onRangeChange?: (range: MarketRange) => void
  className?: string
}

export const MarketCard: React.FC<MarketCardProps> = ({
  tokenLabel = 'ARCS',
  currentPriceLabel = '1.28',
  priceChangeLabel = '5.16+',
  isPriceChangePositive = true,
  data,
  initialRange = '1H',
  onRangeChange,
  className,
}) => {
  const [selectedRange, setSelectedRange] = useState<MarketRange>(initialRange)
  const chartContainerRef = useRef<HTMLDivElement | null>(null)
  const chartApiRef = useRef<IChartApi | null>(null)
  const seriesRef = useRef<ISeriesApi<'Area'> | null>(null)

  const seriesData = useMemo(() => {
    const fallback = DEFAULT_DATA[selectedRange]
    const customSeries = data?.[selectedRange]
    if (customSeries && customSeries.length > 0) {
      return customSeries
    }
    return fallback
  }, [data, selectedRange])

  useEffect(() => {
    const container = chartContainerRef.current
    if (!container) return undefined

    const chart = createChart(container, {
      width: container.clientWidth,
      height: container.clientHeight,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#7b849b',
        fontFamily: 'Inter, "Noto Sans JP", sans-serif',
      },
      grid: {
        vertLines: { visible: false },
        horzLines: { visible: false },
      },
      rightPriceScale: {
        borderVisible: false,
        visible: false,
      },
      timeScale: {
        borderVisible: false,
        secondsVisible: false,
        visible: false,
      },
      crosshair: {
        vertLine: { visible: false },
        horzLine: { visible: false },
      },
      watermark: {
        visible: false,
      },
    })

    const areaSeries = chart.addAreaSeries({
      lineColor: '#1f68ff',
      topColor: 'rgba(31, 104, 255, 0.16)',
      bottomColor: 'rgba(31, 104, 255, 0)',
      lineWidth: 2,
      priceLineVisible: false,
    })

    chartApiRef.current = chart
    seriesRef.current = areaSeries

    const hideBrandingAndAxes = () => {
      const brandingLink = container.querySelector<HTMLAnchorElement>('a[href*="tradingview.com"]')
      if (brandingLink) {
        brandingLink.style.display = 'none'
      }
      const axes = container.querySelectorAll<HTMLElement>(
        '.tv-lightweight-charts-price-axis, .tv-lightweight-charts-time-axis'
      )
      for (const axis of axes) {
        axis.style.display = 'none'
      }
    }

    hideBrandingAndAxes()
    const brandingObserver = new MutationObserver(hideBrandingAndAxes)
    brandingObserver.observe(container, { childList: true, subtree: true })

    const resizeObserver = new ResizeObserver(() => {
      if (!chartContainerRef.current) return
      const { clientWidth, clientHeight } = chartContainerRef.current
      chart.applyOptions({ width: clientWidth, height: clientHeight })
      chart.timeScale().fitContent()
    })
    resizeObserver.observe(container)

    return () => {
      resizeObserver.disconnect()
      brandingObserver.disconnect()
      chart.remove()
      chartApiRef.current = null
      seriesRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!seriesRef.current) return
    seriesRef.current.setData(seriesData)
    chartApiRef.current?.timeScale().fitContent()
  }, [seriesData])

  const handleSelectRange = (range: MarketRange) => {
    setSelectedRange(range)
    onRangeChange?.(range)
  }

  return (
    <div
      className={`bg-white flex w-full flex-col rounded-[24px] shadow-[0_4px_8px_rgba(0,0,0,0.02)] ${className ?? ''}`}
    >
      <div className="flex items-center gap-2 px-4 py-6">
        <ArcsTokenIcon />
        <p className="m-0 text-[16px] font-semibold text-[#1f1f1f] leading-[1.3]">{tokenLabel}</p>
      </div>
      <div className="flex items-start gap-4 px-4 pb-6">
        <DataStatistic
          className="flex grow basis-0"
          label="現在の価格"
          value={currentPriceLabel}
          prefix="¥"
        />
        <div className="flex items-end justify-center self-stretch">
          <span className="h-8 w-px bg-[#f6f6f6]" aria-hidden="true" />
        </div>
        <DataStatistic
          className="flex grow basis-0"
          label="前日比"
          value={priceChangeLabel}
          prefix="¥"
          tone={isPriceChangePositive ? 'positive' : 'negative'}
        />
      </div>
      <div className="flex flex-col gap-2 px-0 pb-6">
        <div className="h-[122px] w-full" ref={chartContainerRef} />
        <div className="flex items-center justify-center gap-2 px-4">
          {TAB_ORDER.map((range) => {
            const isActive = range === selectedRange
            return (
              <button
                key={range}
                type="button"
                onClick={() => handleSelectRange(range)}
                className={`rounded-full px-3 py-1.5 text-sm font-mono tracking-[-0.28px] transition-colors ${isActive ? 'bg-[#1f1f1f] text-white' : 'text-[#7b849b] hover:text-[#1f1f1f]'}`}
              >
                {range}
              </button>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default MarketCard
